/**
 * Modern Button Component
 * Clean, consistent button styles
 */

import { ReactNode, ButtonHTMLAttributes } from 'react'
import { Link } from 'react-router-dom'
import styles from './Button.module.css'

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success' | 'ai'
export type ButtonSize = 'sm' | 'md' | 'lg' | 'small' | 'large' // small/large are aliases for sm/lg

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant
  size?: ButtonSize
  icon?: ReactNode
  fullWidth?: boolean
  loading?: boolean
  as?: 'button' | 'link'
  to?: string
}

export default function Button({
  children,
  variant = 'primary',
  size = 'md',
  icon,
  fullWidth = false,
  loading = false,
  disabled,
  className = '',
  as = 'button',
  to,
  ...props
}: ButtonProps) {
  // Normalize size aliases
  const normalizedSize = size === 'small' ? 'sm' : size === 'large' ? 'lg' : size
  const classes = `${styles.button} ${styles[variant]} ${styles[normalizedSize]} ${
    fullWidth ? styles.fullWidth : ''
  } ${loading ? styles.loading : ''} ${className}`

  if (as === 'link' && to) {
    return (
      <Link to={to} className={classes}>
        {loading && <span className={styles.spinner} />}
        {icon && <span className={styles.icon}>{icon}</span>}
        {children}
      </Link>
    )
  }

  return (
    <button className={classes} disabled={disabled || loading} {...props}>
      {loading && <span className={styles.spinner} />}
      {icon && <span className={styles.icon}>{icon}</span>}
      {children}
    </button>
  )
}

